import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  SimpleGrid,
  VStack,
  HStack,
  Link,
  Flex,
  keyframes,
  useColorModeValue,
  Badge,
  Icon,
  Image,
} from '@chakra-ui/react';
import { FaArrowRight, FaGithub, FaRobot, FaCode, FaBrain, FaUsers, FaHandshake, FaChartLine } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

const Hero: React.FC = () => {
  // 定义游标闪烁动画
  const blinkAnimation = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0; }
    100% { opacity: 1; }
  `;

  // 标题出现动画
  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  // 背景微妙渐变动画
  const gradientAnimation = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;

  const bgGradient = useColorModeValue(
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(124,58,237,0.05) 100%)',
    'linear-gradient(180deg, rgba(26,32,44,0) 0%, rgba(124,58,237,0.1) 100%)'
  );

  return (
    <Box py={20} mt="80px" position="relative" overflow="hidden" bgGradient={bgGradient}>
      {/* 背景装饰元素 */}
      <Box
        position="absolute"
        width="600px"
        height="600px"
        borderRadius="full"
        bgGradient="radial-gradient(circle, rgba(124,58,237,0.1) 0%, rgba(124,58,237,0.05) 100%)"
        top="-300px"
        right="-300px"
        zIndex={0}
      />
      <Box
        position="absolute"
        width="300px"
        height="300px"
        borderRadius="full"
        bgGradient="radial-gradient(circle, rgba(124,58,237,0.1) 0%, rgba(124,58,237,0.01) 100%)"
        bottom="-150px"
        left="-150px"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        {/* 主标题区域 */}
        <Box
          textAlign="center"
          mb={16}
          sx={{
            '& > *': {
              animation: `${fadeIn} 0.8s ease-out forwards`,
            }
          }}
        >
          <Badge
            colorScheme="purple"
            mb={4}
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
            boxShadow="0 4px 6px rgba(124, 58, 237, 0.1)"
            sx={{ animationDelay: '0.1s' }}
          >
            领先的AI解决方案提供商
          </Badge>
          <Flex justifyContent="center" alignItems="baseline" sx={{ animationDelay: '0.3s' }}>
            <Heading
              as="h1"
              size={{ base: "3xl", md: "4xl" }}
              fontWeight="extrabold"
              mb={4}
              bgGradient="linear(to-r, brand.purple, purple.500)"
              backgroundSize="200% 200%"
              animation={`${gradientAnimation} 8s ease infinite`}
              bgClip="text"
              letterSpacing="-1px"
              display="inline"
            >
              {'{ Y-Square }'}
            </Heading>
            <Box
              as="span"
              color="brand.purple"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="extrabold"
              ml={1}
              display="inline-block"
              animation={`${blinkAnimation} 1.2s step-end infinite`}
              lineHeight="1"
              mb={4}
            >
              AI
            </Box>
          </Flex>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="700px"
            mx="auto"
            mb={6}
            sx={{ animationDelay: '0.5s' }}
            lineHeight="1.7"
          >
            专注于人工智能研发和服务，为您的业务提供智能化解决方案
          </Text>

          {/* 按钮组 */}
          <Flex
            justify="center"
            align="center"
            gap={5}
            mb={16}
            flexWrap={{ base: "wrap", md: "nowrap" }}
            sx={{ animationDelay: '0.7s' }}
          >
            <Button
              as={RouterLink}
              to="/solutions"
              size="lg"
              colorScheme="purple"
              fontSize="md"
              py={6}
              px={8}
              boxShadow="0 10px 15px -3px rgba(124, 58, 237, 0.3)"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 15px 20px -3px rgba(124, 58, 237, 0.4)",
              }}
              transition="all 0.3s ease"
              rightIcon={<FaArrowRight />}
              bgGradient="linear(to-r, brand.purple, purple.600)"
            >
              了解我们的服务
            </Button>
            <Button
              as={RouterLink}
              to="/contact"
              size="lg"
              variant="outline"
              fontSize="md"
              py={6}
              px={8}
              borderColor="brand.purple"
              color="brand.purple"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 4px 6px -1px rgba(124, 58, 237, 0.1)",
                bg: "purple.50"
              }}
              transition="all 0.3s ease"
              leftIcon={<FaUsers />}
            >
              联系我们
            </Button>
          </Flex>
        </Box>

        {/* 特性区域 */}
        <Box mb={20}>
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={12}
            color="brand.purple"
            fontWeight="bold"
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              width: "60px",
              height: "4px",
              bg: "brand.purple",
              bottom: "-10px",
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: "full"
            }}
          >
            为什么选择我们
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 8, md: 10 }}>
            <VStack
              align="start"
              p={8}
              borderRadius="xl"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              borderTop="4px solid"
              borderTopColor="brand.purple"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(124, 58, 237, 0.1)"
              }}
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="50px"
                h="50px"
                borderRadius="lg"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={3}
              >
                <Icon as={FaBrain} color="white" boxSize={5} />
              </Flex>
              <Heading as="h3" size="md" mb={4} color="brand.purple" fontWeight="bold">
                专业研发团队
              </Heading>
              <Text color="gray.600" lineHeight="1.8">
                拥有来自顶尖高校和科技公司的AI专家团队，在机器学习、深度学习和自然语言处理等领域拥有丰富经验。
              </Text>
            </VStack>

            <VStack
              align="start"
              p={8}
              borderRadius="xl"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              borderTop="4px solid"
              borderTopColor="brand.purple"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(124, 58, 237, 0.1)"
              }}
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="50px"
                h="50px"
                borderRadius="lg"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={3}
              >
                <Icon as={FaHandshake} color="white" boxSize={5} />
              </Flex>
              <Heading as="h3" size="md" mb={4} color="brand.purple" fontWeight="bold">
                定制化解决方案
              </Heading>
              <Text color="gray.600" lineHeight="1.8">
                根据客户特定需求提供量身定制的AI解决方案，从需求分析到实施部署，提供全流程专业服务支持。
              </Text>
            </VStack>

            <VStack
              align="start"
              p={8}
              borderRadius="xl"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              borderTop="4px solid"
              borderTopColor="brand.purple"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(124, 58, 237, 0.1)"
              }}
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="50px"
                h="50px"
                borderRadius="lg"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={3}
              >
                <Icon as={FaChartLine} color="white" boxSize={5} />
              </Flex>
              <Heading as="h3" size="md" mb={4} color="brand.purple" fontWeight="bold">
                成果驱动
              </Heading>
              <Text color="gray.600" lineHeight="1.8">
                注重实际业务价值，通过AI技术帮助客户提升效率、降低成本、优化流程，创造可衡量的商业价值。
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>

        {/* 案例展示区域 */}
        <Box mb={16}>
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={12}
            color="brand.purple"
            fontWeight="bold"
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              width: "60px",
              height: "4px",
              bg: "brand.purple",
              bottom: "-10px",
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: "full"
            }}
          >
            成功案例
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            <VStack
              align="start"
              p={0}
              borderRadius="xl"
              overflow="hidden"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)"
              }}
            >
              <Box
                w="full"
                h="200px"
                bgGradient="linear(to-r, brand.purple, purple.400)"
                position="relative"
                overflow="hidden"
              >
                <Badge
                  position="absolute"
                  top={4}
                  left={4}
                  colorScheme="purple"
                  fontSize="xs"
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                  zIndex={1}
                >
                  知识库平台
                </Badge>
                <Box
                  bgImage="url('https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60')"
                  bgSize="cover"
                  bgPosition="center"
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  opacity={0.7}
                />
              </Box>
              <Box p={6} w="full">
                <Heading as="h3" size="md" mb={2} color="brand.purple" fontWeight="bold">
                  企业智能知识库平台
                </Heading>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  客户：某国有大型金融机构
                </Text>
                <Text color="gray.600" noOfLines={3} lineHeight="1.7" mb={4}>
                  构建面向员工的智能知识库系统，整合内部规章制度、产品手册和专业知识，实现智能检索，将信息查找时间缩短85%，员工培训效率提升60%。
                </Text>
                <Link
                  as={RouterLink}
                  to="/cases"
                  color="brand.purple"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  _hover={{ textDecoration: "none" }}
                >
                  查看详情 <Icon as={FaArrowRight} ml={2} boxSize={3} />
                </Link>
              </Box>
            </VStack>

            <VStack
              align="start"
              p={0}
              borderRadius="xl"
              overflow="hidden"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)"
              }}
            >
              <Box
                w="full"
                h="200px"
                bgGradient="linear(to-r, brand.purple, purple.400)"
                position="relative"
                overflow="hidden"
              >
                <Badge
                  position="absolute"
                  top={4}
                  left={4}
                  colorScheme="purple"
                  fontSize="xs"
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                  zIndex={1}
                >
                  图像识别
                </Badge>
                <Box
                  bgImage="url('https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60')"
                  bgSize="cover"
                  bgPosition="center"
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  opacity={0.7}
                />
              </Box>
              <Box p={6} w="full">
                <Heading as="h3" size="md" mb={2} color="brand.purple" fontWeight="bold">
                  工业产品缺陷检测系统
                </Heading>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  客户：某电子制造企业
                </Text>
                <Text color="gray.600" noOfLines={3} lineHeight="1.7" mb={4}>
                  开发基于计算机视觉的产品缺陷检测系统，实时分析产品质量，识别准确率达99.2%，减少人工检测成本65%，产品退货率降低42%。
                </Text>
                <Link
                  as={RouterLink}
                  to="/cases"
                  color="brand.purple"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  _hover={{ textDecoration: "none" }}
                >
                  查看详情 <Icon as={FaArrowRight} ml={2} boxSize={3} />
                </Link>
              </Box>
            </VStack>

            <VStack
              align="start"
              p={0}
              borderRadius="xl"
              overflow="hidden"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)"
              }}
            >
              <Box
                w="full"
                h="200px"
                bgGradient="linear(to-r, brand.purple, purple.400)"
                position="relative"
                overflow="hidden"
              >
                <Badge
                  position="absolute"
                  top={4}
                  left={4}
                  colorScheme="purple"
                  fontSize="xs"
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                  zIndex={1}
                >
                  数据分析
                </Badge>
                <Box
                  bgImage="url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60')"
                  bgSize="cover"
                  bgPosition="center"
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  opacity={0.7}
                />
              </Box>
              <Box p={6} w="full">
                <Heading as="h3" size="md" mb={2} color="brand.purple" fontWeight="bold">
                  智能数据分析平台
                </Heading>
                <Text fontSize="sm" color="gray.500" mb={3}>
                  客户：某互联网企业
                </Text>
                <Text color="gray.600" noOfLines={3} lineHeight="1.7" mb={4}>
                  为客户打造数据分析平台，整合多渠道数据源，构建可视化分析工具，助力精准营销决策，客户转化率提升28%，营销投入产出比提高45%。
                </Text>
                <Link
                  as={RouterLink}
                  to="/cases"
                  color="brand.purple"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  _hover={{ textDecoration: "none" }}
                >
                  查看详情 <Icon as={FaArrowRight} ml={2} boxSize={3} />
                </Link>
              </Box>
            </VStack>
          </SimpleGrid>
        </Box>

        {/* 联系信息区域 */}
        <Box
          mb={16}
          p={10}
          borderRadius="xl"
          boxShadow="0 10px 30px rgba(0,0,0,0.05)"
          bg="white"
        >
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={10}
            color="brand.purple"
            fontWeight="bold"
          >
            联系我们
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <VStack
              p={6}
              borderRadius="lg"
              bg="gray.50"
              align="center"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
              }}
              transition="all 0.3s ease"
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="60px"
                h="60px"
                borderRadius="full"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={4}
              >
                <Icon as={FaUsers} color="white" boxSize={6} />
              </Flex>
              <Heading as="h3" size="md" mb={2} color="brand.purple">
                公司地址
              </Heading>
              <Text color="gray.600" textAlign="center">
                深圳市龙华区民治街道上芬社区<br/>
                第五工业区一区28号303
              </Text>
            </VStack>

            <VStack
              p={6}
              borderRadius="lg"
              bg="gray.50"
              align="center"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
              }}
              transition="all 0.3s ease"
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="60px"
                h="60px"
                borderRadius="full"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={4}
              >
                <Icon as={FaCode} color="white" boxSize={6} />
              </Flex>
              <Heading as="h3" size="md" mb={2} color="brand.purple">
                服务热线
              </Heading>
              <Text color="gray.600" textAlign="center">
                电话/微信: 18617190008<br />
                联系人: 曹经理
              </Text>
            </VStack>

            <VStack
              p={6}
              borderRadius="lg"
              bg="gray.50"
              align="center"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
              }}
              transition="all 0.3s ease"
            >
              <Flex
                bgGradient="linear(to-br, brand.purple, purple.400)"
                w="60px"
                h="60px"
                borderRadius="full"
                justify="center"
                align="center"
                boxShadow="0 4px 10px rgba(124, 58, 237, 0.3)"
                mb={4}
              >
                <Icon as={FaRobot} color="white" boxSize={6} />
              </Flex>
              <Heading as="h3" size="md" mb={2} color="brand.purple">
                合作咨询
              </Heading>
              <Text color="gray.600" textAlign="center">
                业务咨询: y_square_biz@163.com<br />
                联系人: 曹经理
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;